import { worldToChunkCoordinate } from "./world-generation.js";
export const INTERACTION_ACTION_IDS = Object.freeze(["inspect", "open", "access", "take", "use"]);
export const INTERACTION_TARGET_KINDS = Object.freeze(["WORLD_OBJECT", "DROPPED_ITEM"]);
export const INTERACTION_PROFILE_IDS = Object.freeze(["supply_cache", "dropped_item"]);
export const INTERACTION_OBJECT_STATES = Object.freeze(["CLOSED", "OPEN", "AVAILABLE", "REMOVED"]);
export const INTERACTION_RESULT_CODES = Object.freeze([
    "OK",
    "INVALID_PLAYER",
    "INVALID_OBJECT",
    "INVALID_ACTION",
    "INVALID_REQUEST",
    "TOO_FAR",
    "OCCLUDED",
    "BUSY",
    "STATE_CHANGED",
    "ALREADY_OPEN",
    "CANCELLED",
    "OUT_OF_RANGE",
    "RATE_LIMITED",
    "INVENTORY_FULL",
    "TOO_HEAVY",
    "ITEM_GONE",
]);
export const SUPPLY_CACHE_INTERACTION_PROFILE = Object.freeze({
    id: "supply_cache",
    displayName: "EMERGENCY CACHE",
    initialState: "CLOSED",
    actions: Object.freeze([
        Object.freeze({
            id: "open",
            label: "OPEN",
            durationMs: 750,
            maxRange: 2.25,
            requiresLineOfSight: true,
            priority: 100,
            allowedStates: Object.freeze(["CLOSED"]),
            interruptible: true,
            transitionTo: "OPEN",
        }),
        Object.freeze({
            id: "access",
            label: "ACCESS",
            durationMs: 0,
            maxRange: 2.25,
            requiresLineOfSight: true,
            priority: 100,
            allowedStates: Object.freeze(["OPEN"]),
            interruptible: true,
        }),
        Object.freeze({
            id: "inspect",
            label: "INSPECT",
            durationMs: 0,
            maxRange: 2.25,
            requiresLineOfSight: true,
            priority: 90,
            allowedStates: Object.freeze(["OPEN"]),
            interruptible: true,
        }),
    ]),
});
export const DROPPED_ITEM_INTERACTION_PROFILE = Object.freeze({
    id: "dropped_item",
    displayName: "DROPPED ITEM",
    initialState: "AVAILABLE",
    actions: Object.freeze([
        Object.freeze({
            id: "take",
            label: "TAKE",
            durationMs: 0,
            maxRange: 2,
            requiresLineOfSight: false,
            priority: 120,
            allowedStates: Object.freeze(["AVAILABLE"]),
            interruptible: true,
        }),
    ]),
});
export const INTERACTION_PROFILES = Object.freeze({
    supply_cache: SUPPLY_CACHE_INTERACTION_PROFILE,
    dropped_item: DROPPED_ITEM_INTERACTION_PROFILE,
});
const INTERACTABLE_ASSETS = Object.freeze({
    supply_cache_closed: Object.freeze({
        profileId: "supply_cache",
        localAnchor: Object.freeze([0, 0.62, 0.52]),
        interactionRadius: 0.3,
    }),
});
export function getInteractionProfile(profileId) {
    return INTERACTION_PROFILES[profileId];
}
export function getAvailableInteractionActions(profile, state) {
    return profile.actions.filter((action) => action.allowedStates.includes(state));
}
/** Explicit asset metadata is the only route from a generated object to interaction. */
export function getInteractableDescriptor(object) {
    const definition = INTERACTABLE_ASSETS[object.assetId];
    if (!definition)
        return undefined;
    return Object.freeze({
        objectId: object.id,
        profileId: definition.profileId,
        localAnchor: definition.localAnchor,
        worldAnchor: transformInteractionAnchor(object, definition.localAnchor),
        interactionRadius: definition.interactionRadius * object.scale,
        ownerChunk: Object.freeze([
            worldToChunkCoordinate(object.x),
            worldToChunkCoordinate(object.z),
        ]),
    });
}
/** Matches the engine's upright model matrix, including its yaw sign convention. */
export function transformInteractionAnchor(object, localAnchor) {
    const cosine = Math.cos(object.yaw);
    const sine = Math.sin(object.yaw);
    const localX = localAnchor[0] * object.scale;
    const localY = localAnchor[1] * object.scale;
    const localZ = localAnchor[2] * object.scale;
    return Object.freeze([
        object.x + localX * cosine + localZ * sine,
        object.y + localY,
        object.z - localX * sine + localZ * cosine,
    ]);
}
export function getInteractionDistance(origin, target) {
    return Math.max(0, Math.hypot(target.worldAnchor[0] - origin[0], target.worldAnchor[1] - origin[1], target.worldAnchor[2] - origin[2]) - target.interactionRadius);
}
/**
 * Shared simple-proxy LOS. Current static colliders have no vertical bounds, so
 * their XZ shapes are intentionally treated as vertical extrusions.
 */
export function isInteractionLineOccluded(origin, target, targetObjectId, colliders) {
    for (const collider of colliders) {
        if (collider.id === targetObjectId)
            continue;
        if (segmentIntersectsCollider(origin[0], origin[2], target[0], target[2], collider))
            return true;
    }
    return false;
}
export function hasInteractionLineOfSight(origin, target, targetObjectId, colliders) {
    return !isInteractionLineOccluded(origin, target, targetObjectId, colliders);
}
function segmentIntersectsCollider(startX, startZ, endX, endZ, collider) {
    if (collider.shape === "circle") {
        return pointSegmentDistanceSquared(collider.x, collider.z, startX, startZ, endX, endZ) <= collider.radius * collider.radius;
    }
    const cosine = Math.cos(collider.yaw);
    const sine = Math.sin(collider.yaw);
    const toLocal = (x, z) => {
        const offsetX = x - collider.x;
        const offsetZ = z - collider.z;
        return [offsetX * cosine - offsetZ * sine, offsetX * sine + offsetZ * cosine];
    };
    const localStart = toLocal(startX, startZ);
    const localEnd = toLocal(endX, endZ);
    if (collider.shape === "box") {
        return segmentIntersectsAxisAlignedBox(localStart[0], localStart[1], localEnd[0], localEnd[1], collider.halfWidth, collider.halfDepth);
    }
    return segmentSegmentDistanceSquared(localStart[0], localStart[1], localEnd[0], localEnd[1], -collider.halfLength, 0, collider.halfLength, 0) <= collider.radius * collider.radius;
}
function segmentIntersectsAxisAlignedBox(startX, startZ, endX, endZ, halfWidth, halfDepth) {
    let minimum = 0;
    let maximum = 1;
    const deltaX = endX - startX;
    const deltaZ = endZ - startZ;
    const clip = (origin, delta, extent) => {
        if (Math.abs(delta) < 1e-12)
            return Math.abs(origin) <= extent;
        const first = (-extent - origin) / delta;
        const second = (extent - origin) / delta;
        const near = Math.min(first, second);
        const far = Math.max(first, second);
        minimum = Math.max(minimum, near);
        maximum = Math.min(maximum, far);
        return minimum <= maximum;
    };
    return clip(startX, deltaX, halfWidth) && clip(startZ, deltaZ, halfDepth);
}
function pointSegmentDistanceSquared(pointX, pointZ, startX, startZ, endX, endZ) {
    const deltaX = endX - startX;
    const deltaZ = endZ - startZ;
    const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
    const amount = lengthSquared > 1e-12
        ? Math.max(0, Math.min(1, ((pointX - startX) * deltaX + (pointZ - startZ) * deltaZ) / lengthSquared))
        : 0;
    const differenceX = pointX - (startX + deltaX * amount);
    const differenceZ = pointZ - (startZ + deltaZ * amount);
    return differenceX * differenceX + differenceZ * differenceZ;
}
function segmentSegmentDistanceSquared(aStartX, aStartZ, aEndX, aEndZ, bStartX, bStartZ, bEndX, bEndZ) {
    if (segmentsIntersect(aStartX, aStartZ, aEndX, aEndZ, bStartX, bStartZ, bEndX, bEndZ))
        return 0;
    return Math.min(pointSegmentDistanceSquared(aStartX, aStartZ, bStartX, bStartZ, bEndX, bEndZ), pointSegmentDistanceSquared(aEndX, aEndZ, bStartX, bStartZ, bEndX, bEndZ), pointSegmentDistanceSquared(bStartX, bStartZ, aStartX, aStartZ, aEndX, aEndZ), pointSegmentDistanceSquared(bEndX, bEndZ, aStartX, aStartZ, aEndX, aEndZ));
}
function segmentsIntersect(aStartX, aStartZ, aEndX, aEndZ, bStartX, bStartZ, bEndX, bEndZ) {
    const cross = (ax, az, bx, bz, cx, cz) => ((bx - ax) * (cz - az) - (bz - az) * (cx - ax));
    const first = cross(aStartX, aStartZ, aEndX, aEndZ, bStartX, bStartZ);
    const second = cross(aStartX, aStartZ, aEndX, aEndZ, bEndX, bEndZ);
    const third = cross(bStartX, bStartZ, bEndX, bEndZ, aStartX, aStartZ);
    const fourth = cross(bStartX, bStartZ, bEndX, bEndZ, aEndX, aEndZ);
    if (first * second < 0 && third * fourth < 0)
        return true;
    const onSegment = (start, end, value) => (value >= Math.min(start, end) - 1e-12 && value <= Math.max(start, end) + 1e-12);
    if (Math.abs(first) <= 1e-12 && onSegment(aStartX, aEndX, bStartX) && onSegment(aStartZ, aEndZ, bStartZ))
        return true;
    if (Math.abs(second) <= 1e-12 && onSegment(aStartX, aEndX, bEndX) && onSegment(aStartZ, aEndZ, bEndZ))
        return true;
    if (Math.abs(third) <= 1e-12 && onSegment(bStartX, bEndX, aStartX) && onSegment(bStartZ, bEndZ, aStartZ))
        return true;
    return Math.abs(fourth) <= 1e-12 && onSegment(bStartX, bEndX, aEndX) && onSegment(bStartZ, bEndZ, aEndZ);
}
